import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

function blankRule() {
  return {
    filter_type: "include",
    jobs: {
      from: "",
      to: "",
      matching: "",
      class: "",
      status: "active",
      wbs_type: "",
    },
    phases: { from: "", to: "", matching: "", status: "open" },
    categories: {
      from: "",
      to: "",
      matching: "",
      status: "open",
      require_labor_budget: false,
    },
  };
}

export default function JobsFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            jobs: {
              from: "",
              to: "",
              matching: "",
              class: "",
              wbs_type: "",
              ...r.jobs,
              status: r.jobs?.status ?? "",
            },
            phases: {
              from: "",
              to: "",
              matching: "",
              ...r.phases,
              status: r.phases?.status ?? "",
            },
            categories: {
              from: "",
              to: "",
              matching: "",
              require_labor_budget: false,
              ...r.categories,
              status: r.categories?.status ?? "",
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, path, value) {
    setRules((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        if (path.length === 1) return { ...r, [path[0]]: value };
        return { ...r, [path[0]]: { ...r[path[0]], [path[1]]: value } };
      })
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      jobs: {
        from: r.jobs.from || null,
        to: r.jobs.to || null,
        matching: r.jobs.matching || null,
        class: r.jobs.class || null,
        status: r.jobs.status || null,
        wbs_type: r.jobs.wbs_type || null,
      },
      phases: {
        from: r.phases.from || null,
        to: r.phases.to || null,
        matching: r.phases.matching || null,
        status: r.phases.status || null,
      },
      categories: {
        from: r.categories.from || null,
        to: r.categories.to || null,
        matching: r.categories.matching || null,
        status: r.categories.status || null,
        require_labor_budget: r.categories.require_labor_budget,
      },
    }));

    onSave({ resource: "jobs", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div
        className="jfm-container jfm-container--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jfm-header">
          <span className="jfm-title">Jobs Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => {
              const phasesDisabled = rule.filter_type === "exclude_jobs";
              const categoriesDisabled =
                rule.filter_type === "exclude_jobs" ||
                rule.filter_type === "exclude_phases";
              return (
                <div key={idx} className="jfm-row">
                  <select
                    className="jfm-type-select"
                    value={rule.filter_type}
                    onChange={(e) =>
                      updateRule(idx, ["filter_type"], e.target.value)
                    }
                  >
                    <option value="include">Include</option>
                    <option value="exclude_categories">
                      Exclude Categories
                    </option>
                    <option value="exclude_phases">Exclude Phases</option>
                    <option value="exclude_jobs">Exclude Jobs</option>
                  </select>

                  <div className="jfm-panels">
                    <div className="jfm-panel">
                      <div className="jfm-panel-title">JOBS</div>
                      <div className="jfm-field">
                        <label>From</label>
                        <SearchableCodeSelect
                          value={rule.jobs.from}
                          companyUuid={companyUuid}
                          placeholder="(first)"
                          fetchOptions={FieldConfigurationAPI.getJobs}
                          emptyMessage="No jobs found"
                          disabled={readOnly}
                          onChange={(code) =>
                            updateRule(idx, ["jobs", "from"], code)
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>To</label>
                        <SearchableCodeSelect
                          value={rule.jobs.to}
                          companyUuid={companyUuid}
                          placeholder="(last)"
                          fetchOptions={FieldConfigurationAPI.getJobs}
                          emptyMessage="No jobs found"
                          disabled={readOnly}
                          onChange={(code) =>
                            updateRule(idx, ["jobs", "to"], code)
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Matching</label>
                        <input
                          value={rule.jobs.matching}
                          placeholder="(all)"
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["jobs", "matching"],
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Class</label>
                        <input
                          value={rule.jobs.class}
                          placeholder="(all)"
                          onChange={(e) =>
                            updateRule(idx, ["jobs", "class"], e.target.value)
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Status</label>
                        <select
                          value={rule.jobs.status ?? ""}
                          onChange={(e) =>
                            updateRule(idx, ["jobs", "status"], e.target.value)
                          }
                        >
                          <option value="">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="closed">Closed</option>
                          <option value="active_inactive">
                            Active+Inactive
                          </option>
                          <option value="inactive_closed">
                            Inactive+Closed
                          </option>
                        </select>
                      </div>
                      <div className="jfm-field">
                        {/* "T&M" matches job.wbs_type directly; "non-t&m" covers Job/Phase/Cat and Job/Cat. */}
                        <label>Type</label>
                        <select
                          value={rule.jobs.wbs_type || ""}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["jobs", "wbs_type"],
                              e.target.value
                            )
                          }
                        >
                          <option value="">All</option>
                          <option value="T&M">T&M</option>
                          <option value="non-t&m">Non-T&M</option>
                        </select>
                      </div>
                    </div>

                    <div
                      className={`jfm-panel${
                        phasesDisabled ? " jfm-panel--disabled" : ""
                      }`}
                    >
                      <div className="jfm-panel-title">PHASES</div>
                      <div className="jfm-field">
                        <label>From</label>
                        <input
                          value={rule.phases.from}
                          placeholder="(first)"
                          disabled={phasesDisabled}
                          onChange={(e) =>
                            updateRule(idx, ["phases", "from"], e.target.value)
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>To</label>
                        <input
                          value={rule.phases.to}
                          placeholder="(last)"
                          disabled={phasesDisabled}
                          onChange={(e) =>
                            updateRule(idx, ["phases", "to"], e.target.value)
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Matching</label>
                        <input
                          value={rule.phases.matching}
                          placeholder="(all)"
                          disabled={phasesDisabled}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["phases", "matching"],
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Status</label>
                        <select
                          value={rule.phases.status ?? ""}
                          disabled={phasesDisabled}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["phases", "status"],
                              e.target.value
                            )
                          }
                        >
                          <option value="open">Open</option>
                          <option value="closed">Closed</option>
                          <option value="">All</option>
                        </select>
                      </div>
                    </div>

                    <div
                      className={`jfm-panel${
                        categoriesDisabled ? " jfm-panel--disabled" : ""
                      }`}
                    >
                      <div className="jfm-panel-title">CATEGORIES</div>
                      <div className="jfm-field">
                        <label>From</label>
                        <input
                          value={rule.categories.from}
                          placeholder="(first)"
                          disabled={categoriesDisabled}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["categories", "from"],
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>To</label>
                        <input
                          value={rule.categories.to}
                          placeholder="(last)"
                          disabled={categoriesDisabled}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["categories", "to"],
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Matching</label>
                        <input
                          value={rule.categories.matching}
                          placeholder="(all)"
                          disabled={categoriesDisabled}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["categories", "matching"],
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="jfm-field">
                        <label>Status</label>
                        <select
                          value={rule.categories.status ?? ""}
                          disabled={categoriesDisabled}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              ["categories", "status"],
                              e.target.value
                            )
                          }
                        >
                          <option value="open">Open</option>
                          <option value="closed">Closed</option>
                          <option value="">All</option>
                        </select>
                      </div>
                      <div className="jfm-field jfm-field--checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={rule.categories.require_labor_budget}
                            disabled={categoriesDisabled}
                            onChange={(e) =>
                              updateRule(
                                idx,
                                ["categories", "require_labor_budget"],
                                e.target.checked
                              )
                            }
                          />
                          Require Labor Budget
                        </label>
                      </div>
                    </div>
                  </div>

                  {!readOnly && (
                    <button
                      className="jfm-delete"
                      onClick={() => removeRule(idx)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
